import { useState, useEffect } from "react";
import { WordSelectionItem } from "../../types/game";
import "./AllTogetherMode.css";
import { CompletionModal } from "../ui/CompletionModal";
import { GamePanelProps } from "../ui/GameContent";
import { shuffle } from "../../utils/utils";
import useGameStore from "../../context/GameContext";
import { QuestionList } from "../game/QuestionItem";

export const AllTogetherMode = ({ items, libraryName }: GamePanelProps) => {
  // Get timer functions from store
  const { pauseTimer, getFormattedTime, gameId } = useGameStore();
  // 使用状态来跟踪每个问题的选择
  const [selectedOptions, setSelectedOptions] = useState<{
    [key: number]: string;
  }>({});
  const [submittedAnswers, setSubmittedAnswers] = useState(false);
  const [wrongAnswers, setWrongAnswers] = useState<WordSelectionItem[]>([]);
  const [shuffledQuestions, setShuffledQuestions] = useState<
    WordSelectionItem[]
  >([]);
  const [questionsWithOptions, setQuestionsWithOptions] = useState<
    { question: WordSelectionItem; options: string[] }[]
  >([]);

  // 临时消息和结果相关状态
  const [tempMessage, setTempMessage] = useState<{
    text: string;
    color: string;
  } | null>(null);
  const [showCompletionModal, setShowCompletionModal] = useState(false);
  const [completionTime, setCompletionTime] = useState(0);

  const initGame = () => {
    setSelectedOptions({});
    setSubmittedAnswers(false);
    setWrongAnswers([]);
    setShowCompletionModal(false);

    const shuffled = shuffle([...items]);
    setShuffledQuestions(shuffled); // 为每个问题准备打乱的选项
    const withOptions = shuffled.map((question) => ({
      question,
      options: shuffle([...question.options]),
    }));

    setQuestionsWithOptions(withOptions);
  };

  // 初始化问题和选项
  useEffect(() => {
    initGame();
  }, [items, gameId]);

  // 处理选项选择
  const handleOptionClick = (questionIndex: number, optionValue: string) => {
    if (submittedAnswers) return; // 如果已经提交，禁止更改选择

    // 更新状态，记录哪个问题选了哪个答案
    setSelectedOptions((prev) => ({
      ...prev,
      [questionIndex]: optionValue,
    }));
  };

  // 显示临时消息的函数
  const showTempMessage = (message: string, color: string) => {
    setTempMessage({ text: message, color });
    setTimeout(() => setTempMessage(null), 3000);
  };

  // 提交所有答案
  const submitAllAnswers = () => {
    try {
      // 重置错题记录
      const newWrongAnswers: WordSelectionItem[] = [];

      let allAnswered = true;

      // 检查所有问题是否已回答
      shuffledQuestions.forEach((question, index) => {
        if (!selectedOptions[index]) {
          allAnswered = false;
        }
      });

      if (!allAnswered) {
        showTempMessage("请先完成所有题目的作答！", "#dc3545");
        return;
      }

      // 检查答案并记录错题
      shuffledQuestions.forEach((question, index) => {
        const selected = selectedOptions[index];
        const isCorrect = selected === question.correctAnswer;

        if (!isCorrect) {
          newWrongAnswers.push(question);
        }
      });

      // 更新错题列表
      setWrongAnswers(newWrongAnswers);
      setSubmittedAnswers(true);

      // 调用外部函数
      pauseTimer();
      // 计算时间（将格式化的时间转换为秒）
      const timeStr = getFormattedTime();
      const timeParts = timeStr.split(":");
      let totalSeconds = 0;

      if (timeParts.length === 2) {
        // MM:SS 格式
        totalSeconds = parseInt(timeParts[0]) * 60 + parseInt(timeParts[1]);
      } else if (timeParts.length === 3) {
        // HH:MM:SS 格式
        totalSeconds =
          parseInt(timeParts[0]) * 3600 +
          parseInt(timeParts[1]) * 60 +
          parseInt(timeParts[2]);
      }

      setCompletionTime(totalSeconds);

      // 显示完成模态框
      setShowCompletionModal(true);
    } catch (error) {
      console.error("提交出错:", error);
      showTempMessage("提交过程中出现错误", "#dc3545");
    }
  };

  // 重置游戏状态
  const handlePlayAgain = () => {
    initGame();

    // 重置并启动计时器
    useGameStore.getState().resetTimer();
    useGameStore.getState().startTimer();
  };

  // 关闭模态框
  const handleCloseModal = () => {
    setShowCompletionModal(false);
  };

  return (
    <div className="all-together-container">
      {/* 临时消息显示 */}
      {tempMessage && (
        <div
          className="temp-message"
          style={{ backgroundColor: tempMessage.color }}
        >
          {tempMessage.text}
        </div>
      )}

      {/* 完成后的模态框 */}
      {showCompletionModal && (
        <CompletionModal
          time={completionTime}
          missedWords={wrongAnswers}
          onClose={handleCloseModal}
          onPlayAgain={handlePlayAgain}
          mode="allTogether"
        />
      )}

      <QuestionList
        questionsWithOptions={questionsWithOptions}
        selectedOptions={selectedOptions}
        submittedAnswers={submittedAnswers}
        handleOptionClick={handleOptionClick}
      />

      <div className="submit-container">
        <button
          id="submitAll"
          className="submit-button"
          onClick={submitAllAnswers}
          disabled={submittedAnswers}
        >
          提交答案
        </button>
      </div>
    </div>
  );
};
